import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useAdminAuth } from "../../legacy/context/AdminAuthContext.js";
import { resolveAdminLandingPath } from "../../legacy/admin/utils/adminNavigation.js";

export default function AdminLogin() {
  const { register, handleSubmit } = useForm();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [twoFactorToken, setTwoFactorToken] = useState("");
  const [tempToken, setTempToken] = useState("");
  const [pendingAdmin, setPendingAdmin] = useState(null);
  const { login, verifyTwoFactor } = useAdminAuth();

  const onSubmit = async (values) => {
    setError("");
    const payload = {
      email: String(values.email || "").trim(),
      password: String(values.password || ""),
    };
    try {
      setLoading(true);
      const result = await login(payload.email, payload.password);
      if (result?.twoFactorRequired) {
        setTwoFactorRequired(true);
        setTempToken(result.tempToken || "");
        setPendingAdmin(result.admin || null);
        return;
      }
      const nextUser = result?.admin || result || {};
      navigate(
        resolveAdminLandingPath({
          user: nextUser,
          isSuperAdmin: Boolean(result?.isSuperAdmin || nextUser?.isSuperAdmin),
          hasPermission: (permission) => {
            const permissions = result?.permissions || nextUser?.permissions || [];
            return Array.isArray(permission)
              ? permission.some((entry) => permissions.includes(entry))
              : permissions.includes(permission);
          },
        }),
        { replace: true }
      );
    } catch (err) {
      const message = err?.message || "Invalid credentials. Please try again.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyTwoFactor = async (event) => {
    event.preventDefault();
    setError("");
    try {
      setLoading(true);
      const result = await verifyTwoFactor(tempToken, String(twoFactorToken || "").trim());
      const nextUser = result?.admin || result || {};
      navigate(
        resolveAdminLandingPath({
          user: nextUser,
          isSuperAdmin: Boolean(result?.isSuperAdmin || nextUser?.isSuperAdmin),
          hasPermission: (permission) => {
            const permissions = result?.permissions || nextUser?.permissions || [];
            return Array.isArray(permission)
              ? permission.some((entry) => permissions.includes(entry))
              : permissions.includes(permission);
          },
        }),
        { replace: true }
      );
    } catch (err) {
      const message =
        err?.message ||
        "Invalid two-factor code. Please try again.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const resetTwoFactor = () => {
    setTwoFactorRequired(false);
    setTwoFactorToken("");
    setTempToken("");
    setPendingAdmin(null);
    setError("");
  };

  return (
    <div className="min-h-screen grid place-items-center bg-sand px-6">
      <div className="bg-white rounded-3xl shadow-soft-lg p-8 w-full max-w-md">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">
          Homevilas Admin
        </p>
        <h1 className="text-2xl font-display text-deep mt-3">
          Sign in to manage stays
        </h1>
        {!twoFactorRequired ? (
          <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4">
            <input
              {...register("email")}
              type="email"
              placeholder="Email"
              className="w-full px-4 py-3 rounded-2xl border border-slate-200"
            />
            <input
              {...register("password")}
              type="password"
              placeholder="Password"
              className="w-full px-4 py-3 rounded-2xl border border-slate-200"
            />
            {error && <p className="text-sm text-red-500">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-3 rounded-2xl bg-deep text-white text-xs uppercase tracking-[0.3em] disabled:opacity-60"
            >
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyTwoFactor} className="mt-6 space-y-4">
            <div className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-xs text-slate-500">
              Enter the 6-digit code from your authenticator app for{" "}
              <span className="text-slate-700">
                {pendingAdmin?.email || "your account"}
              </span>
              .
            </div>
            <input
              value={twoFactorToken}
              onChange={(event) => setTwoFactorToken(event.target.value)}
              placeholder="Two-factor code"
              className="w-full px-4 py-3 rounded-2xl border border-slate-200"
            />
            {error && <p className="text-sm text-red-500">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-3 rounded-2xl bg-deep text-white text-xs uppercase tracking-[0.3em] disabled:opacity-60"
            >
              {loading ? "Verifying..." : "Verify & continue"}
            </button>
            <button
              type="button"
              onClick={resetTwoFactor}
              className="w-full px-4 py-2 rounded-2xl border border-slate-200 text-xs uppercase tracking-[0.3em] text-slate-500"
            >
              Back to login
            </button>
          </form>
        )}
        <p className="text-xs text-slate-500 mt-4">
          Use your assigned admin credentials to access the production dashboard.
        </p>
      </div>
    </div>
  );
}
